<template>
  <div class="air__utils__content">
    <div class="air__utils__heading">
      <div class="row align-items-center">
        <div class="col-md-6 col-12 mb-2 mb-md-0">
          <h5 class="mb-0 font-weight-bold">
            <i class="fe fe-users mr-2 text-primary"></i> Zoom Class Attendance Logs
          </h5>
          <small class="text-muted">Track which students joined online classes, join/leave timestamps, and active duration</small>
        </div>
        <div class="col-md-6 col-12 text-md-right">
          <a-button type="default" icon="download" @click="exportToCSV" :disabled="attendances.length === 0">
            Export CSV
          </a-button>
        </div>
      </div>
    </div>

    <!-- Metric Summary Cards -->
    <div class="row mb-4">
      <div class="col-md-4 col-sm-6 col-12 mb-3 mb-md-0">
        <a-card :bordered="false" class="shadow-sm border-left border-primary border-3">
          <div class="d-flex align-items-center">
            <div class="mr-3">
              <div class="avatar avatar--lg bg-light text-primary rounded-circle p-3 d-flex align-items-center justify-content-center" style="width: 48px; height: 48px;">
                <i class="fe fe-log-in font-size-20"></i>
              </div>
            </div>
            <div>
              <div class="text-muted text-uppercase font-size-12 font-weight-bold">Total Join Events</div>
              <div class="font-size-24 font-weight-bold text-dark">{{ summary.total_joins || 0 }}</div>
            </div>
          </div>
        </a-card>
      </div>

      <div class="col-md-4 col-sm-6 col-12 mb-3 mb-md-0">
        <a-card :bordered="false" class="shadow-sm border-left border-success border-3">
          <div class="d-flex align-items-center">
            <div class="mr-3">
              <div class="avatar avatar--lg bg-light text-success rounded-circle p-3 d-flex align-items-center justify-content-center" style="width: 48px; height: 48px;">
                <i class="fe fe-user-check font-size-20"></i>
              </div>
            </div>
            <div>
              <div class="text-muted text-uppercase font-size-12 font-weight-bold">Unique Students</div>
              <div class="font-size-24 font-weight-bold text-dark">{{ summary.unique_students || 0 }}</div>
            </div>
          </div>
        </a-card>
      </div>

      <div class="col-md-4 col-sm-6 col-12">
        <a-card :bordered="false" class="shadow-sm border-left border-info border-3">
          <div class="d-flex align-items-center">
            <div class="mr-3">
              <div class="avatar avatar--lg bg-light text-info rounded-circle p-3 d-flex align-items-center justify-content-center" style="width: 48px; height: 48px;">
                <i class="fe fe-clock font-size-20"></i>
              </div>
            </div>
            <div>
              <div class="text-muted text-uppercase font-size-12 font-weight-bold">Avg. Class Duration</div>
              <div class="font-size-24 font-weight-bold text-dark">{{ summary.avg_duration_minutes || 0 }} <small class="font-size-14 text-muted">mins</small></div>
            </div>
          </div>
        </a-card>
      </div>
    </div>

    <!-- Filter Card -->
    <a-card class="mb-4" :bordered="false">
      <div class="row">
        <!-- Date Range Filter -->
        <div class="col-md-3 col-sm-6 col-12 mb-3">
          <label class="font-weight-bold text-dark mb-1">Date Range</label>
          <a-range-picker
            v-model="dateRange"
            style="width: 100%"
            format="YYYY-MM-DD"
            @change="handleDateChange"
          />
        </div>

        <!-- Class Filter -->
        <div class="col-md-3 col-sm-6 col-12 mb-3">
          <label class="font-weight-bold text-dark mb-1">Class</label>
          <a-select v-model="filterClass" style="width: 100%" @change="handleFilterClassChange" placeholder="All Classes" allowClear>
            <a-select-option :value="0">All Classes</a-select-option>
            <a-select-option v-for="c in schoolClasses" :key="c.id" :value="c.id">
              {{ c.name }}
            </a-select-option>
          </a-select>
        </div>

        <!-- Section Filter -->
        <div class="col-md-2 col-sm-6 col-12 mb-3">
          <label class="font-weight-bold text-dark mb-1">Section</label>
          <a-select v-model="filterSection" style="width: 100%" @change="fetchData" placeholder="All Sections" :disabled="!filterClass || filterClass === 0" allowClear>
            <a-select-option :value="0">All Sections</a-select-option>
            <a-select-option v-for="sec in filterSections" :key="sec.id" :value="sec.id">
              {{ sec.name }}
            </a-select-option>
          </a-select>
        </div>

        <!-- Search Box -->
        <div class="col-md-4 col-sm-6 col-12 mb-3">
          <label class="font-weight-bold text-dark mb-1">Search Student</label>
          <a-input-search v-model="searchQuery" placeholder="Name or Admission No" @search="fetchData" enterButton />
        </div>
      </div>
    </a-card>

    <!-- Attendance Table -->
    <a-card :bordered="false" class="shadow-sm">
      <div class="table-responsive">
        <a-table
          :columns="columns"
          :dataSource="attendances"
          :rowKey="record => record.id"
          :loading="tableLoading"
          :pagination="pagination"
          @change="handleTableChange"
        >
          <!-- Date -->
          <template slot="date" slot-scope="text, record">
            <span class="font-weight-bold text-dark">{{ formatDate(record.attendance_date) }}</span>
          </template>

          <!-- Student Name & Admission No -->
          <template slot="student" slot-scope="text, record">
            <div class="d-flex align-items-center">
              <a-avatar size="small" icon="user" class="mr-2 bg-primary" />
              <div>
                <div class="font-weight-bold text-dark">{{ record.display_name }}</div>
                <div v-if="record.enrollment && record.enrollment.student" class="text-muted font-size-12">
                  Adm No: {{ record.enrollment.student.admission_no || '-' }}
                </div>
              </div>
            </div>
          </template>

          <!-- Class & Section -->
          <template slot="classSection" slot-scope="text, record">
            <span v-if="record.meeting_link && record.meeting_link.school_class">
              {{ record.meeting_link.school_class.name }}
              <span v-if="record.meeting_link.section" class="badge badge-light border ml-1">
                Sec {{ record.meeting_link.section.name }}
              </span>
            </span>
            <span v-else class="text-muted">-</span>
          </template>

          <!-- Subject & Teacher -->
          <template slot="subject" slot-scope="text, record">
            <div v-if="record.meeting_link">
              <div class="font-weight-bold text-primary">
                {{ record.meeting_link.subject ? record.meeting_link.subject.name : '-' }}
              </div>
              <div v-if="record.meeting_link.teacher" class="text-muted font-size-12">
                Teacher: {{ record.meeting_link.teacher.first_name }} {{ record.meeting_link.teacher.last_name }}
              </div>
            </div>
            <span v-else class="text-muted">-</span>
          </template>

          <!-- Join & Leave Time -->
          <template slot="time" slot-scope="text, record">
            <div><i class="fe fe-arrow-right text-success mr-1"></i> {{ formatTime(record.joined_at) }}</div>
            <div v-if="record.left_at" class="text-muted font-size-12">
              <i class="fe fe-arrow-left text-danger mr-1"></i> {{ formatTime(record.left_at) }}
            </div>
          </template>

          <!-- Duration -->
          <template slot="duration" slot-scope="text, record">
            <span v-if="record.duration_seconds > 0" class="badge badge-success font-size-12 px-2 py-1">
              {{ Math.round(record.duration_seconds / 60) }} mins
            </span>
            <span v-else-if="record.joined_at && !record.left_at" class="badge badge-warning font-size-12 px-2 py-1">
              In Session
            </span>
            <span v-else class="text-muted">-</span>
          </template>

          <!-- Device -->
          <template slot="device" slot-scope="text, record">
            <span v-if="record.device_type === 'app'" class="badge badge-light border text-dark">
              <i class="fe fe-smartphone mr-1"></i> App
            </span>
            <span v-else class="badge badge-light border text-dark">
              <i class="fe fe-globe mr-1"></i> Web
            </span>
          </template>
        </a-table>
      </div>
    </a-card>
  </div>
</template>

<script>
import moment from 'moment'

export default {
  data() {
    return {
      tableLoading: false,
      dateRange: [moment().subtract(7, 'days'), moment()],
      filterClass: 0,
      filterSection: 0,
      searchQuery: '',
      schoolClasses: [],
      filterSections: [],
      attendances: [],
      summary: {
        total_joins: 0,
        unique_students: 0,
        avg_duration_minutes: 0,
      },
      pagination: {
        current: 1,
        pageSize: 25,
        total: 0,
        showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} records`,
      },
      columns: [
        { title: 'Date', scopedSlots: { customRender: 'date' }, width: 120 },
        { title: 'Student', scopedSlots: { customRender: 'student' } },
        { title: 'Class & Section', scopedSlots: { customRender: 'classSection' }, width: 160 },
        { title: 'Subject & Teacher', scopedSlots: { customRender: 'subject' }, width: 220 },
        { title: 'Join / Leave', scopedSlots: { customRender: 'time' }, width: 160 },
        { title: 'Duration', scopedSlots: { customRender: 'duration' }, width: 120 },
        { title: 'Device', scopedSlots: { customRender: 'device' }, width: 100 },
      ],
    }
  },
  mounted() {
    // Read query params from route if coming from meetings list
    if (this.$route.query.class_id) {
      this.filterClass = Number(this.$route.query.class_id)
    }
    if (this.$route.query.section_id) {
      this.filterSection = Number(this.$route.query.section_id)
    }
    this.fetchClasses()
    this.fetchData()
    this.fetchReport()
  },
  methods: {
    formatDate(date) {
      return date ? moment(date).format('DD MMM YYYY') : '-'
    },
    formatTime(datetime) {
      return datetime ? moment(datetime).format('hh:mm A') : '-'
    },

    fetchClasses() {
      this.$http.get('/api/class').then(({ data }) => {
        this.schoolClasses = data.data || []
      }).catch(() => {})
    },

    handleFilterClassChange(val) {
      this.filterClass = val
      this.filterSection = 0
      if (val && val !== 0) {
        this.$http.get(`/api/section/class/${val}`).then(({ data }) => {
          this.filterSections = data.data || []
        })
      } else {
        this.filterSections = []
      }
      this.pagination.current = 1
      this.fetchData()
    },

    handleDateChange() {
      this.pagination.current = 1
      this.fetchData()
      this.fetchReport()
    },

    handleTableChange(pagination) {
      this.pagination.current = pagination.current
      this.fetchData()
    },

    fetchData() {
      this.tableLoading = true
      const params = {
        page: this.pagination.current,
        per_page: this.pagination.pageSize,
      }

      if (this.dateRange && this.dateRange.length === 2) {
        params.start_date = this.dateRange[0].format('YYYY-MM-DD')
        params.end_date = this.dateRange[1].format('YYYY-MM-DD')
      }
      if (this.filterClass) params.class_id = this.filterClass
      if (this.filterSection) params.section_id = this.filterSection
      if (this.searchQuery) params.search = this.searchQuery

      this.$http.get('/api/zoom/attendance', { params })
        .then(({ data }) => {
          this.attendances = data.data || []
          this.pagination.total = data.total || 0
        })
        .catch(err => {
          console.error(err)
          this.$notification.error({
            message: 'Error',
            description: 'Failed to load Zoom attendance logs.',
          })
        })
        .finally(() => {
          this.tableLoading = false
        })
    },

    fetchReport() {
      const params = {}
      if (this.dateRange && this.dateRange.length === 2) {
        params.start_date = this.dateRange[0].format('YYYY-MM-DD')
        params.end_date = this.dateRange[1].format('YYYY-MM-DD')
      }

      this.$http.get('/api/zoom/attendance/report', { params })
        .then(({ data }) => {
          if (data && data.data) {
            this.summary = data.data
          }
        })
        .catch(() => {})
    },

    exportToCSV() {
      if (this.attendances.length === 0) return

      let csv = 'Date,Student Name,Admission No,Class,Section,Subject,Teacher,Join Time,Leave Time,Duration (Mins),Device\n'
      this.attendances.forEach(row => {
        const date = row.attendance_date || ''
        const name = (row.display_name || '').replace(/,/g, ' ')
        const adm = (row.enrollment && row.enrollment.student && row.enrollment.student.admission_no) || ''
        const className = (row.meeting_link && row.meeting_link.school_class && row.meeting_link.school_class.name) || ''
        const section = (row.meeting_link && row.meeting_link.section && row.meeting_link.section.name) || 'All'
        const subject = (row.meeting_link && row.meeting_link.subject && row.meeting_link.subject.name) || ''
        const teacher = (row.meeting_link && row.meeting_link.teacher && (row.meeting_link.teacher.first_name + ' ' + row.meeting_link.teacher.last_name)) || ''
        const join = row.joined_at ? moment(row.joined_at).format('hh:mm A') : ''
        const leave = row.left_at ? moment(row.left_at).format('hh:mm A') : ''
        const duration = row.duration_seconds ? Math.round(row.duration_seconds / 60) : 0
        const device = row.device_type || 'app'

        csv += `"${date}","${name}","${adm}","${className}","${section}","${subject}","${teacher}","${join}","${leave}","${duration}","${device}"\n`
      })

      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
      const url = URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.setAttribute('href', url)
      link.setAttribute('download', `zoom_attendance_${moment().format('YYYYMMDD_HHmm')}.csv`)
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
    },
  },
}
</script>
